import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, PhoneCall, RefreshCcw, Copy, Share2, ShieldCheck } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";

/**
 * /settings/call-id — Slice K · Universal Find2Near Call ID.
 *
 * Every user gets a shared 8-digit code. Anyone can "call" that code from
 * the Find2Near web app and it routes to the owner's mobile — the caller
 * never sees the raw number, the owner never publishes it.
 */
export default function CallId() {
  const [data, setData]     = useState(null);
  const [loading, setLoad]  = useState(true);
  const [rotating, setRot]  = useState(false);
  useSeo({ title: "My Call ID — Find2Near" });

  const load = async () => {
    setLoad(true);
    try {
      const r = await api.get("/call/me");
      setData(r.data);
    } catch {
      toast.error("Please sign in to see your call id");
    } finally {
      setLoad(false);
    }
  };
  useEffect(() => { load(); }, []);

  const regenerate = async () => {
    if (!window.confirm("Rotate your call ID? The old one will stop working.")) return;
    setRot(true);
    try {
      const r = await api.post("/call/regenerate");
      setData(d => ({ ...d, call_id: r.data.call_id }));
      toast.success("New call ID assigned");
    } catch {
      toast.error("Couldn't rotate the code");
    } finally {
      setRot(false);
    }
  };

  const origin  = typeof window !== "undefined" ? window.location.origin : "";
  const shareUrl = data?.call_id ? `${origin}/call/${data.call_id}` : null;

  const copy = () => {
    if (!data?.call_id) return;
    navigator.clipboard.writeText(data.call_id).then(() => toast.success("Call ID copied"));
  };
  const share = async () => {
    if (!shareUrl) return;
    if (navigator.share) {
      try { await navigator.share({ title: "Reach me on Find2Near", url: shareUrl }); } catch { /* cancelled */ }
    } else {
      navigator.clipboard.writeText(shareUrl).then(() => toast.success("Share link copied"));
    }
  };

  return (
    <div className="max-w-md mx-auto min-h-screen bg-white" data-testid="call-id-page">
      <div className="flex items-center gap-3 px-4 pt-5 pb-4 border-b border-stone-100">
        <Link to="/account" className="p-2 -ml-2 rounded-full hover:bg-stone-100" data-testid="callid-back">
          <ArrowLeft size={20} />
        </Link>
        <h1 className="heading font-black text-2xl text-stone-900">My Call ID</h1>
      </div>

      <div className="p-4 space-y-4">
        <div className="rounded-2xl bg-emerald-50 border border-emerald-100 p-4 flex gap-3">
          <PhoneCall size={22} className="text-emerald-700 shrink-0 mt-0.5" />
          <div className="text-sm text-emerald-900">
            Share this <b>Find2Near Call ID</b> instead of your phone number.
            Anyone who has it can reach you — we bridge the call so your number stays private.
          </div>
        </div>

        {loading && <div className="text-center text-stone-400 py-8">Loading…</div>}

        {!loading && data?.call_id && (
          <>
            <div className="rounded-2xl border border-stone-200 p-6 text-center">
              <div className="text-xs font-bold uppercase tracking-widest text-stone-500 mb-1">Your Call ID</div>
              <div
                className="text-4xl font-black tracking-widest text-stone-900 tabular-nums"
                data-testid="call-id-value"
              >
                {data.call_id}
              </div>
              {data.forward_masked && (
                <div className="text-xs text-stone-500 mt-2 inline-flex items-center gap-1">
                  <ShieldCheck size={12} /> Routes to {data.forward_masked}
                </div>
              )}

              {shareUrl && (
                <div className="mt-4 flex justify-center">
                  <div className="p-3 bg-white border border-stone-200 rounded-xl">
                    <QRCodeCanvas value={shareUrl} size={168} includeMargin={false} data-testid="call-id-qr" />
                  </div>
                </div>
              )}

              <div className="mt-4 grid grid-cols-3 gap-2">
                <button onClick={copy} className="rounded-full border border-stone-300 text-sm font-bold py-2 inline-flex items-center justify-center gap-1" data-testid="call-id-copy">
                  <Copy size={13} /> Copy
                </button>
                <button onClick={share} className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold py-2 inline-flex items-center justify-center gap-1" data-testid="call-id-share">
                  <Share2 size={13} /> Share
                </button>
                <button
                  onClick={regenerate}
                  disabled={rotating}
                  className="rounded-full border border-rose-300 text-rose-700 disabled:opacity-50 text-sm font-bold py-2 inline-flex items-center justify-center gap-1"
                  data-testid="call-id-rotate"
                >
                  <RefreshCcw size={13} /> {rotating ? "…" : "Rotate"}
                </button>
              </div>
            </div>

            <div className="text-xs text-stone-500 text-center">
              Anyone can dial your Call ID at <span className="font-mono">{origin.replace(/^https?:\/\//, "")}/call/{data.call_id}</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
